import {
  type ChangeEvent,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';

import './YesNoSelect.scss';

import NoIcon from 'src/assets/no-icon.svg?react';
import YesIcon from 'src/assets/yes-icon.svg?react';
import { cNs } from 'src/helpers';

type CheckBoxProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export type YesNoSelectProps = Omit<CheckBoxProps, 'onChange' | 'type' | 'hidden'> & {
  label?: string;
  description?: string | null;

  yesLabel?: string;
  noLabel?: string;

  danger?: boolean | null;

  onChange?: (checked: boolean | null) => void;
};

export function YesNoSelect({
  label,
  description,
  noLabel = 'No',
  yesLabel = 'Yes',
  danger,
  onChange,
  ...checkBoxProps
}: YesNoSelectProps) {
  const [isYesSelected, setIsYesSelected] = useState<boolean | null>(
    typeof checkBoxProps.value === 'boolean' ? checkBoxProps.value : null
  );

  const setValue = useCallback(
    (newValue: boolean | null) => {
      setIsYesSelected(newValue);

      onChange?.(newValue);
    },
    [onChange]
  );

  const handleYesClick = useCallback(() => {
    setValue(isYesSelected ? null : true);
  }, [isYesSelected, setValue]);

  const handleNoClick = useCallback(() => {
    setValue(isYesSelected === false ? null : false);
  }, [isYesSelected, setValue]);

  const handleCheckedChange = useCallback(
    (event: ChangeEvent) => {
      const checked = (event.target as HTMLInputElement).checked;

      if (isYesSelected !== checked) {
        setValue(checked);
      }
    },
    [isYesSelected, setValue]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    if (checkBoxProps.value == null && isYesSelected !== null) {
      setIsYesSelected(null);
    } else if (typeof checkBoxProps.value === 'boolean' && checkBoxProps.value !== isYesSelected) {
      setIsYesSelected(checkBoxProps.value);
    }
  }, [checkBoxProps.value]);

  return (
    <div className={cNs('yes-no-select__container', checkBoxProps.className, danger && 'yes-no-select__danger')}>
      <input {...checkBoxProps} type="checkbox" hidden={true} onChange={handleCheckedChange} />
      {label && <div className="yes-no-select__title">{label}</div>}
      {description && <div className="yes-no-select__description">{description}</div>}

      <div className="yes-no-select__choices-container">
        <div
          onClick={checkBoxProps.disabled ? undefined : handleYesClick}
          className={cNs('yes-no-select__label', checkBoxProps.disabled && 'yes-no-select__label-disabled')}
        >
          <YesIcon className={cNs('yes-no-select__icon', isYesSelected && 'yes-no-select__selected')} />
          <span>{yesLabel}</span>
        </div>
        <div
          onClick={checkBoxProps.disabled ? undefined : handleNoClick}
          className={cNs('yes-no-select__label', checkBoxProps.disabled && 'yes-no-select__label-disabled')}
        >
          <NoIcon className={cNs('yes-no-select__icon', isYesSelected === false && 'yes-no-select__selected')} />
          <span>{noLabel}</span>
        </div>
      </div>
    </div>
  );
}
